<template>
  <canvas
    ref="canvas"
    class="rive"
    :style="{
      '--rive-width': $attrs.width as number,
      '--rive-height': $attrs.height as number,
    }"
  ></canvas>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'
import { Alignment, Fit, Layout, Rive } from '@rive-app/canvas'
import type { Event as RiveEvent, EventCallback } from '@rive-app/canvas'
import { emitter } from '@/services/mitt'
import { assertNotNull } from '@/helpers/typing'

/**
 * This component is a wrapper around the Rive webplayer.
 *
 * The props in this component are a subset of the parameters in the Rive constructor
 * that we actually use. Check Rive's documentation for the complete list of parameters
 * and their descriptions: https://rive.app/docs/runtimes/web/rive-parameters
 *
 * Rive's methods and properties can be accessed through the `rive` ref, which is exposed
 * in this component. The link above also has documentation on Rive's properties and methods.
 */
const props = withDefaults(
  defineProps<{
    /** File path to a .riv file. */
    src: string
    /** Name or names of animations to play. @Default first animation in the state machine. */
    animations?: string | string[]
    /** Name or names of state machines to play. */
    stateMachines?: string | string[]
    /** If true, the animation will autoplay. */
    autoplay?: boolean

    /**
     * How the content will be fitted to the view.
     * For more details, see Rive's documentation: https://rive.app/docs/runtimes/layout.
     */
    fit?: keyof typeof Fit
    /** How the content aligns with respect to the view bounds. */
    alignment?: keyof typeof Alignment

    /** Callback that gets fired when the .riv file loads */
    onLoad?: EventCallback
    /** Callback that gets fired when an error occurs loading the .riv file. */
    onLoadError?: EventCallback
    /** Callback that gets fired when the animation starts playing. */
    onPlay?: EventCallback
    /** Callback that gets fired when the animation pauses. */
    onPause?: EventCallback
    /** Callback that gets fired when the animation stops playing. */
    onStop?: EventCallback
    /** Callback that gets fired when the animation completes a loop.. */
    onLoop?: EventCallback
    /** Callback that gets fired when a state change occurs. */
    onStateChange?: EventCallback
    /** Callback that gets fired every frame when the Artboard has advanced. */
    onAdvance?: EventCallback
  }>(),
  {
    autoplay: true,
  },
)

const canvas = ref<HTMLCanvasElement | null>(null)
const rive = shallowRef<Rive | null>(null)
const mediaQueryListener = shallowRef<MediaQueryList | null>(null)
const isLoaded = ref(false)

onMounted(async () => {
  rive.value = new Rive({
    ...props,
    canvas: assertNotNull(canvas.value),
    layout: createLayout(),
    onLoad: (event: RiveEvent): void => {
      isLoaded.value = true
      rive.value?.resetArtboardSize()
      resizeDrawings()
      props.onLoad?.(event)
    },
  })

  // Redraw the canvas when the window is resized.
  emitter.on('windowResize', resizeDrawings)

  // Redraw the canvas when the screen's dpi changes.
  mediaQueryListener.value = window.matchMedia(
    `(resolution: ${window.devicePixelRatio}dppx)`,
  )
  mediaQueryListener.value.addEventListener('change', resizeDrawings)
})

onUnmounted(() => {
  emitter.off('windowResize')
  mediaQueryListener.value?.removeEventListener('change', resizeDrawings)
  rive.value?.removeAllRiveEventListeners()
  rive.value?.cleanup()
})

// Rive isn't reactive, so we need to watch for changes
// in the props and manually update them.
watch(
  () => props.src,
  (newSrc) => {
    isLoaded.value = false
    rive.value?.load({ ...props, src: newSrc })
  },
)

watch(
  () => props.animations,
  (newAnimation) => {
    // Reset the current animation.
    if (rive.value?.isPlaying) {
      rive.value?.reset()
    }

    // Play the new animation. If it's `undefined`,
    // play the default animation.
    rive.value?.play(newAnimation || rive.value.animationNames[0])
  },
)

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
watch([() => props.fit, () => props.alignment], (): void => {
  if (rive.value) {
    rive.value.layout = assertNotNull(createLayout())
    resizeDrawings()
  }
})

function createLayout(): Layout | undefined {
  if (!props.fit && !props.alignment) {
    return
  }

  const fit = props.fit ? Fit[props.fit] : undefined
  const alignment = props.alignment ? Alignment[props.alignment] : undefined

  return new Layout({
    fit,
    alignment,
  })
}

/**
 * This is a wrapper around `rive.resizeDrawingSurfaceToCanvas`,
 * that we use so that we can have a reference to clear the event
 * listeners where we attach it.
 */
function resizeDrawings(): void {
  rive.value?.resizeDrawingSurfaceToCanvas()
}

defineExpose({
  rive,
  isLoaded,
})
</script>

<style lang="scss">
.rive {
  width: 100%;
  height: 100%;
  // Rive updates the canvas width and height when
  // it runs `resizeDrawingSurfaceToCanvas` (which we call
  // when the window is resized or the aspect ratio changes).
  // Sometimes, when the canvas is in a flex container, it will
  // shrink when the screen shrinks, but not grow back when the
  // screen grows back. Here we fix that by setting the aspect-ratio
  // to the original width and height (before Rive updates it).
  aspect-ratio: var(--rive-width) / var(--rive-height);
}
</style>
